"use client";

import { useMountEffect } from "@react-hookz/web";
import outOfLives from "shared/user/lives/outOfLives";
import trackPage from "shared/analytics/mixpanel/trackPage";
import { Todo } from "shared/todos/Todo";
import Setlist from "./__helpers/ui/Setlist";
import useScrollToTodoContainer from "./__helpers/ui/SetlistDetails/useScrollToTodoContainer";
import TodosContextProvider from "shared/todos/TodosContextProvider";
import useAuth from "util/useAuth/useAuth";
import TabContainer from "ui/atoms/Container/TabContainer";
import {
  instrumentPages,
  techniquePages,
} from "app/ear-training/mixing/__helpers/getPageConfig";
import Link from "util/Link";
import * as routes from "routes";
import { GeneralPageLayout } from "ui/templates/PageLayouts";
import GameSelector from "ui/organisms/GameSelector/GameSelector";
import { useState } from "react";
import { GameRound } from "shared/games/types/GameRound";
import { Difficulty } from "shared/games/Difficulty";

interface Props {
  todos: Todo[];
}

export default function EarTrainingIndexClientPage({ todos }: Props) {
  const auth = useAuth();
  const [freePlayGameRound, setFreePlayGameRound] = useState<GameRound>({
    difficulty: Difficulty.easy,
    games: [],
    instruments: [],
    techniques: [],
  });

  useMountEffect(() => {
    trackPage("Dashboard");
  });

  useScrollToTodoContainer();

  return (
    <TodosContextProvider todos={todos}>
      <GeneralPageLayout
        auth={auth}
        className="md:pt-10 pt-6 pb-20"
        navbarProps={{ activeLink: "earTraining" }}
      >
        <div className={"mb-5 md:mb-10"}>
          <Setlist locked={outOfLives(auth)} />
        </div>

        <div className={"mb-5 md:mb-10 text-white"}>
          <GameSelector
            onChange={setFreePlayGameRound}
            value={freePlayGameRound}
            locked={outOfLives(auth)}
          />
        </div>

        <div className={"mb-5 md:mb-10 text-white"}>
          <TabContainer
            title="Mix Ear Training by Instrument"
            locked={outOfLives(auth)}
          >
            <ul
              className={"grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))]"}
            >
              {Object.values(instrumentPages).map(
                ({ title, icon, urlParam }) => (
                  <li key={title} className="flex">
                    <Link
                      className={
                        "flex-1 p-4 hover:scale-105 transition-all active:focus:scale-95"
                      }
                      href={routes.earTraining.mixing.instruments.show(
                        urlParam.instrument
                      )}
                    >
                      <div className={"flex items-center justify-center"}>
                        {icon({ className: "w-20 mb-4" })}
                      </div>

                      <div className={"text-xl text-center"}>{title}</div>
                    </Link>
                  </li>
                )
              )}
            </ul>
          </TabContainer>
        </div>

        <div className={"mb-5 md:mb-10 text-white"}>
          <TabContainer
            title="Mix Ear Training by Mix Technique"
            locked={outOfLives(auth)}
          >
            <ul
              className={"grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))]"}
            >
              {Object.values(techniquePages).map(
                ({ title, icon, urlParam }) => (
                  <li key={title} className="flex">
                    <Link
                      className={
                        "flex-1 p-4 hover:scale-105 transition-all active:focus:scale-95"
                      }
                      href={routes.earTraining.mixing.techniques.show(
                        urlParam.technique
                      )}
                    >
                      <div className={"flex items-center justify-center"}>
                        {icon({ className: "w-20 mb-4" })}
                      </div>

                      <div className={"text-xl text-center"}>{title}</div>
                    </Link>
                  </li>
                )
              )}
            </ul>
          </TabContainer>
        </div>
      </GeneralPageLayout>
    </TodosContextProvider>
  );
}
